import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" },
  }),
};

const publications = [
  {
    title: "gr-ntnisac: An Open GNU Radio Testbed for NTN-ISAC",
    href: "https://pubs.gnuradio.org/index.php/grcon/article/view/204",
    venue: "Proceedings of the 16th GNU Radio Conference, Vol. 11, 2026",
  },
  {
    title: "Experimental Demonstration of Snapshot Differential Positioning with LEO Satellites",
    href: "https://arxiv.org/abs/2603.15908",
    venue: "IEEE Radar Conference, 2026",
    note: "Preprint available on arXiv",
  },
  {
    title: "Underwater cooperative MIMO communications using hybrid acoustic and magnetic induction technique",
    href: "https://www.sciencedirect.com/science/article/abs/pii/S1389128620300165",
    venue: "Computer Networks, Volume 173, 22 May 2020",
  },
  {
    title: "Enabling underwater acoustic cooperative MIMO systems by metamaterial-enhanced magnetic induction",
    href: "https://ieeexplore.ieee.org/abstract/document/8885489",
    venue: "IEEE Wireless Communications and Networking Conference (WCNC), 2019",
  },
  {
    title: "Delay Estimation of Initial Access procedure for 5G mm-Wave Cellular Networks",
    href: "https://ieeexplore.ieee.org/abstract/document/9599885",
    venue: "International Conference on Distributed Computing in Sensor Systems (DCOSS), 2021",
  },
  {
    title:
      "Underwater Distributed MIMO System Using Hybrid Acoustic and Magnetic Induction Techniques",
    href: "https://scholar.google.com/citations?user=aT7kdMMAAAAJ&hl=en",
    venue: "M.S. thesis, University at Buffalo (SUNY), 2019",
  },
];

/**
 * Patent families, one entry per invention (a granted patent and its own published
 * application are the same filing, not two). Verified against the public record on
 * Google Patents / Justia.
 */
const grantedPatents = [
  {
    title:
      "Multicast Message that includes Responses to Radio Resource Requests of Wireless Devices of a Multicast Group",
    number: "US 12,750,869 B2",
    date: "Granted Sep 2026 · filed Mar 2024",
    href: "https://patents.google.com/patent/US20240357621A1/en",
    note: "Published as US 2024/0357621 A1.",
  },
  {
    title: "Managing a User Equipment Connection to a Wireless Network",
    number: "US 12,739,916 B2",
    date: "Granted Sep 2026 · filed Feb 2023",
    href: "https://patents.justia.com/patent/12739916",
    note: "Published as US 2024/0040650 A1.",
  },
  {
    title: "Base Station Management of a Connection of a Wireless Device to a Satellite Network",
    number: "US 12,739,028 B2",
    date: "Granted Sep 2026 · filed Oct 2023",
    href: "https://patents.justia.com/patent/12739028",
    note: "Published as US 2025/0132821 A1.",
  },
  {
    title:
      "Method and Satellite System for Scheduling Forward Link and Return Link Resource Allocations of a Satellite Link",
    number: "US 12,732,987 B2",
    date: "Granted Sep 2026 · filed Dec 2023",
    href: "https://patents.justia.com/patent/12732987",
    note: "Published as US 2025/0220660 A1.",
  },
  {
    title:
      "Dynamic Power Assignment Based on Link Qualities and Total Integrated Transmission Power",
    number: "US 12,659,886 B2",
    date: "Granted Jun 2026 · filed May 2024",
    href: "https://patents.justia.com/patent/12659886",
    note: "Published as US 2025/0324370 A1. Continuation pending as US 2026/0281919 A1.",
  },
  {
    title: "Managing a Connection of a Wireless Device to a Satellite Network",
    number: "US 12,574,107 B2",
    date: "Granted Mar 2026 · filed Oct 2023",
    href: "https://patents.google.com/patent/US12574107B2/en",
    note: "Continuation pending as US 2026/0172107 A1.",
  },
  {
    title: "Control of States of a Wireless Device for Connection to a Satellite Network",
    number: "US 12,490,193 B2",
    date: "Granted Dec 2025 · filed May 2023",
    href: "https://patents.google.com/patent/US12490193B2/en",
  },
  {
    title: "Managing Satellite Network Demand for Emergency Services",
    number: "US 12,356,465 B2",
    date: "Granted Jul 2025 · filed Mar 2023",
    href: "https://patents.google.com/patent/US12356465B2/en",
    note: "Continuation pending as US 2025/0301509 A1.",
  },
];

const patentApplications = [
  {
    title: "Management of Service Continuity of a Cell Area of a Satellite Network",
    number: "US 2026/0222059 A1",
    date: "Filed Jun 2025",
    href: "https://patents.justia.com/patent/20260222059",
  },
  {
    title:
      "Management of an Interface of a Satellite Network Between a Wireless Device and a Service Provider",
    number: "US 2026/0046021 A1 · WO 2026/035435 A1",
    date: "Filed Mar 2025",
    href: "https://patents.google.com/patent/US20260046021A1/en",
  },
];

const pageTitleClass =
  "font-display text-3xl md:text-4xl font-semibold text-foreground border-l-2 border-accent pl-4";

export default function Publications() {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
      className="max-w-6xl mx-auto py-10 md:py-14 px-4 md:px-8"
    >
      <Helmet>
        <title>Patents &amp; Papers | Soham Desai</title>
        <meta name="description" content="Soham Desai's published research and patents in satellite communications, underwater MIMO, 5G mmWave, and wireless positioning." />
      </Helmet>

      <motion.h1 variants={fade} className={`${pageTitleClass} mb-6`}>Patents &amp; Papers</motion.h1>
      <motion.p variants={fade} className="mb-10 text-lg text-muted max-w-prose leading-relaxed">
        For the full list and citations, see{" "}
        <a
          href="https://scholar.google.com/citations?user=aT7kdMMAAAAJ&hl=en"
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent hover:text-accent-hover underline underline-offset-4"
        >
          Google Scholar
        </a>
        .
      </motion.p>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-40px" }}
        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        className="mb-14"
      >
        <motion.h2 variants={fade} className="font-display text-2xl font-semibold text-foreground mb-6">
          Journals and conferences
        </motion.h2>
        <div className="space-y-5">
          {publications.map((publication) => (
            <motion.article
              key={publication.title}
              variants={fade}
              className="bg-surface p-6 rounded-lg border border-border"
            >
              <h3 className="text-lg text-foreground font-medium mb-2 leading-snug">
                {publication.to ? (
                  <Link to={publication.to} className="hover:text-accent transition-colors">
                    {publication.title}
                  </Link>
                ) : (
                  <a
                    href={publication.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    {publication.title}
                  </a>
                )}
              </h3>
              {publication.venue && <p className="text-muted-foreground text-sm">{publication.venue}</p>}
              {publication.note && <p className="text-muted-foreground/80 text-sm mt-2">{publication.note}</p>}
            </motion.article>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-40px" }}
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
      >
        <motion.h2 variants={fade} className="font-display text-2xl font-semibold text-foreground mb-4">
          Patents
        </motion.h2>
        <motion.h3 variants={fade} className="mt-8 text-lg font-semibold text-foreground mb-4">Granted</motion.h3>
        <div className="space-y-4 mb-10">
          {grantedPatents.map((patent) => (
            <motion.div
              key={patent.number}
              variants={fade}
              className="bg-surface p-5 rounded-lg border-l-2 border-accent border border-border pl-5"
            >
              <p className="text-foreground text-base leading-snug">
                <a
                  href={patent.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors"
                >
                  {patent.title}
                </a>
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                <span className="font-mono">{patent.number}</span>
                <span className="mx-2 text-border">·</span>
                {patent.date}
              </p>
              {patent.note && (
                <p className="mt-1.5 text-muted-foreground/80 text-sm">{patent.note}</p>
              )}
            </motion.div>
          ))}
        </div>

        <motion.h3 variants={fade} className="text-lg font-semibold text-foreground mb-4">Applications</motion.h3>
        <div className="space-y-4">
          {patentApplications.map((application) => (
            <motion.div
              key={application.number}
              variants={fade}
              className="bg-surface p-5 rounded-lg border border-border"
            >
              <p className="text-foreground text-base leading-snug">
                <a
                  href={application.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors"
                >
                  {application.title}
                </a>
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                <span className="font-mono">{application.number}</span>
                <span className="mx-2 text-border">·</span>
                {application.date}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-40px" }}
        variants={fade}
        className="mt-14"
      >
        <Link
          to="/projects/satellite-comms/3gpp-standards-work"
          className="group block rounded-lg border border-border bg-surface/60 p-6 hover:border-accent/40 transition-colors"
        >
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-accent/80 mb-3">
            Not on this page
          </p>
          <h2 className="font-display text-xl font-semibold text-foreground group-hover:text-accent transition-colors">
            3GPP standards contributions
          </h2>
          <p className="mt-2 text-muted text-sm leading-relaxed max-w-prose">
            Tdocs written and defended in RAN2, RAN4, CT1 and SA on behalf of Skylo, in the working
            groups that assembled the IoT-NTN specifications. Standards contributions are neither
            papers nor patents, so they have their own write-up rather than a place in the lists
            above.
          </p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-accent text-sm">
            Read the write-up
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </Link>
      </motion.div>
    </motion.div>
  );
}
